import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Award, ShieldCheck, Zap, Mail } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import LeadCaptureModal from './LeadCaptureModal';

const API = process.env.REACT_APP_BACKEND_URL;
const VIDEO_MP4 = `/varotrade_hero_hd_v6.mp4`;
const VIDEO_WEBM = `/varotrade_hero_hd_v6.webm`;
const POSTER_URL = `/varotrade_poster_v5.jpg`;

/**
 * SmartVideo — iOS-safe autoplay.
 */
const SmartVideo = React.memo(({ className = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.setAttribute('autoplay', '');

    const tryPlay = () => {
      try {
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (e) { /* noop */ }
    };
    tryPlay();
    v.addEventListener('loadedmetadata', tryPlay);
    v.addEventListener('loadeddata', tryPlay);
    v.addEventListener('canplay', tryPlay);
    v.addEventListener('canplaythrough', tryPlay);
    v.load();

    const onInteract = () => tryPlay();
    document.addEventListener('touchstart', onInteract, { passive: true });
    document.addEventListener('touchend', onInteract, { passive: true });
    document.addEventListener('click', onInteract);
    const onVis = () => { if (!document.hidden) tryPlay(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      v.removeEventListener('loadedmetadata', tryPlay);
      document.removeEventListener('touchstart', onInteract);
      document.removeEventListener('touchend', onInteract);
      document.removeEventListener('click', onInteract);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
  return (
    <div
      className={`relative w-full h-full ${className}`}
      style={{
        backgroundImage: `url(${POSTER_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#000',
      }}
      onClick={() => ref.current?.play().catch(() => {})}
    >
      <video
        ref={ref}
        poster={POSTER_URL}
        autoPlay
        loop
        muted
        playsInline
        // @ts-ignore — iOS-specific
        // eslint-disable-next-line react/no-unknown-property
        webkit-playsinline=""
        preload="auto"
        disablePictureInPicture
        disableRemotePlayback
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src={VIDEO_WEBM} type="video/webm; codecs=vp9" />
        <source src={VIDEO_MP4} type='video/mp4; codecs="avc1.42E01F"' />
      </video>
    </div>
  );
});

/* ─────────────────────────────────────────────────────────────
 * Sub-components MUST live OUTSIDE the parent component so React
 * doesn't unmount them on every keystroke (which was killing
 * input focus after typing a single character).
 * ───────────────────────────────────────────────────────────── */

const InlineForm = React.memo(({
  mobile = false,
  dark = false,
  email,
  onEmailChange,
  onSubmit,
  isRTL,
}) => (
  <motion.form
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.4 } }}
    onSubmit={onSubmit}
    className={`${mobile ? 'mt-5 w-full' : 'mt-8 w-full max-w-[640px]'} group`}
  >
    {dark ? (
      <div className={`flex items-center gap-2.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="relative flex-1 min-w-0 h-14 bg-white rounded-full flex items-center px-6">
          <input
            type="email"
            value={email}
            onChange={onEmailChange}
            placeholder={isRTL ? 'أدخل بريدك الإلكتروني' : 'Enter Your Email'}
            className={`flex-1 min-w-0 h-full bg-transparent text-ink-900 placeholder:text-ink-500 focus:outline-none text-[15px] ${isRTL ? 'text-right' : 'text-left'}`}
            data-testid="hero-email-input"
          />
          <Mail className="w-5 h-5 text-ink-700 shrink-0" strokeWidth={1.8} />
        </div>
        <button
          type="submit"
          className="shrink-0 h-14 px-8 rounded-full bg-flame-500 hover:bg-flame-600 active:bg-flame-700 text-white font-extrabold text-[14px] uppercase tracking-[0.06em] transition-colors whitespace-nowrap"
          data-testid="hero-signup-btn"
        >
          {isRTL ? 'سجّل الآن' : 'SIGN UP NOW'}
        </button>
      </div>
    ) : (
      <div className="relative bg-white rounded-2xl border-2 border-[rgba(20,15,10,0.10)] focus-within:border-flame-500 focus-within:shadow-[0_0_0_4px_rgba(255,122,0,0.14)] transition-all p-1.5 flex items-center gap-1.5">
        <input
          type="email"
          value={email}
          onChange={onEmailChange}
          placeholder={isRTL ? 'بريدك الإلكتروني' : 'Your email address'}
          className="flex-1 min-w-0 h-12 px-3 text-[15px] bg-transparent text-ink-900 placeholder:text-ink-400 focus:outline-none font-medium"
          data-testid="hero-email-input-mobile"
        />
        <button
          type="submit"
          className="shrink-0 inline-flex items-center justify-center gap-1.5 h-11 px-3.5 text-[12px] rounded-xl bg-flame-500 hover:bg-flame-600 text-white font-bold uppercase tracking-[0.10em] transition-colors whitespace-nowrap"
          data-testid="hero-signup-btn-mobile"
        >
          <Zap className="w-4 h-4 fill-white" />
          {isRTL ? 'سجّل' : 'Sign Up'}
        </button>
      </div>
    )}
  </motion.form>
));

const InnerContentDesktop = ({ isRTL, email, onEmailChange, onSubmit }) => (
  <>
    <motion.h1
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.85, delay: 0.05 } }}
      className={`varo-display font-black text-[clamp(2.5rem,4.5vw,4rem)] tracking-[-0.02em] ${isRTL ? 'leading-[1.15]' : 'leading-[1.0]'}`}
    >
      {isRTL ? (
        <><span className="text-white">VAROTRADES</span> <span className="varo-text-flame">منصة التداول المُطلقة</span><span className="varo-text-flame">*</span></>
      ) : (
        <><span className="text-white">VAROTRADES</span> <span className="varo-text-flame">Ultimate Trading Platform</span><span className="varo-text-flame">*</span></>
      )}
    </motion.h1>

    <motion.p
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.25 } }}
      className="mt-8 max-w-xl text-[15px] text-white/85"
    >
      {isRTL
        ? 'منصة CFD حائزة على جوائز يثق بها أكثر من 5,000,000 متداول.'
        : 'An award-winning CFD platform trusted by 5,000,000+ traders.'}
    </motion.p>

    <InlineForm dark email={email} onEmailChange={onEmailChange} onSubmit={onSubmit} isRTL={isRTL} />
  </>
);

const InnerContent = ({ mobile = false, isRTL, email, onEmailChange, onSubmit }) => (
  <>
    {!mobile && (
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0, transition: { duration: 0.6 } }}
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-flame-200 mb-5 shadow-sm"
      >
        <Award className="w-3.5 h-3.5 text-flame-600" />
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-flame-700">
          {isRTL ? 'وسيط حائز على جوائز' : 'Award-Winning Broker'}
        </span>
      </motion.div>
    )}

    <motion.h1
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.1 } }}
      className={`varo-display ${mobile ? 'text-[clamp(2rem,8.5vw,3rem)]' : 'text-[clamp(2.75rem,6vw,5rem)]'} ${isRTL ? 'leading-[1.12]' : 'leading-[0.95]'}`}
    >
      {isRTL ? (
        <><span className="block text-ink-900">VAROTRADES</span><span className="block varo-text-flame">منصة التداول المُطلقة.</span></>
      ) : (
        <><span className="block text-ink-900">VAROTRADES</span><span className="block varo-text-flame">Ultimate Trading Platform.</span></>
      )}
    </motion.h1>

    <motion.p
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.25 } }}
      className={`mt-4 ${mobile ? 'max-w-full text-[14px]' : 'max-w-lg text-[16px]'} text-ink-700 leading-relaxed`}
    >
      {isRTL
        ? 'تنفيذ بسرعة الضوء، فروقات أسعار من 0.0 نقطة، أكثر من 5 ملايين متداول حول العالم.'
        : 'Light-speed execution, spreads from 0.0 pips, 5M+ traders worldwide.'}
    </motion.p>

    <InlineForm mobile={mobile} email={email} onEmailChange={onEmailChange} onSubmit={onSubmit} isRTL={isRTL} />

    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.55 } }}
      className={`mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 ${isRTL ? 'flex-row-reverse' : ''}`}
    >
      <div className="flex items-center gap-1">
        {[0,1,2,3,4].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-flame-500 text-flame-500" />)}
        <span className="ms-2 text-ink-700 text-[12.5px] font-semibold">4.8 / 5 · Trustpilot</span>
      </div>
      <div className={`flex items-center gap-1.5 text-ink-700 text-[12px] ${isRTL ? 'flex-row-reverse' : ''}`}>
        <ShieldCheck className="w-3.5 h-3.5 text-flame-500" />
        {isRTL ? 'مرخّص FSC Mauritius · GB21026396' : 'FSC Mauritius · GB21026396'}
      </div>
    </motion.div>
  </>
);

/**
 * VAROTRADES Hero — Vantage Premium, mobile-perfected.
 */
const VarotradeHero = () => {
  const { isRTL } = useLanguage();
  const [email, setEmail] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  // Stable callbacks so InlineForm stays referentially equal across renders.
  const onEmailChange = useCallback((e) => setEmail(e.target.value), []);
  const onSubmit = useCallback((e) => {
    e.preventDefault();
    setModalOpen(true);
  }, []);

  return (
    <>
      {/* ═════════ DESKTOP HERO ═════════ */}
      <section
        className="relative w-full overflow-hidden bg-black hidden lg:block"
        style={{ height: 'min(560px, 70vh)' }}
        dir="ltr"
        data-testid="hero-section-desktop"
      >
        <div className="absolute inset-0 z-0">
          <SmartVideo />
          <div className={`absolute inset-y-0 ${isRTL ? 'right-0' : 'left-0'} w-[62%] ${
            isRTL ? 'bg-gradient-to-l from-black/70 via-black/40 to-transparent'
                  : 'bg-gradient-to-r from-black/70 via-black/40 to-transparent'
          }`} />
        </div>

        <div className="relative z-10 h-full max-w-[1440px] mx-auto px-10">
          <div className="h-full flex items-center">
            <div
              className={`w-full max-w-[620px] ${isRTL ? 'ml-auto text-right' : 'mr-auto text-left'}`}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <InnerContentDesktop
                isRTL={isRTL}
                email={email}
                onEmailChange={onEmailChange}
                onSubmit={onSubmit}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═════════ MOBILE HERO ═════════ */}
      <section className="relative w-full bg-[#FAF7F2] lg:hidden" data-testid="hero-section-mobile">
        <div className="relative w-full" data-testid="hero-mobile-video-wrap">
          <div className="relative aspect-[16/10] w-full overflow-hidden bg-black">
            <SmartVideo />
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.5, duration: 0.6 } }}
              className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/92 backdrop-blur-md border border-flame-200 shadow-md`}
            >
              <Award className="w-3 h-3 text-flame-600" />
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-flame-700">
                {isRTL ? 'وسيط حائز على جوائز' : 'Award-Winning'}
              </span>
            </motion.div>
          </div>
        </div>

        <div className="relative z-10 px-5 pt-3 pb-10">
          <InnerContent
            mobile
            isRTL={isRTL}
            email={email}
            onEmailChange={onEmailChange}
            onSubmit={onSubmit}
          />
        </div>
      </section>

      <LeadCaptureModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialEmail={email}
      />
    </>
  );
};

export default VarotradeHero;
